import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChevronIcon from '@mui/icons-material/ExpandMoreOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';

import './filtros.css';

/**
 * LOS FILTROS DE LA BIBLIOTECA.
 *
 * Faina, 26-ago: «hagamos unos filtros que permitan al oyente buscar lo que
 * necesita; los filtros son claves. Básate en los mejores filtros de mercado y
 * adáptalo a lo que necesitamos».
 *
 * QUÉ SE TOMÓ DEL PATRÓN ESTÁNDAR (el de cualquier catálogo grande — tiendas,
 * plataformas de vídeo, bibliotecas):
 *
 *   1. UNA BARRA DE DESPLEGABLES, no listas abiertas. Cuatro botones ocupan una
 *      línea; las 35 opciones abiertas ocupaban dos pantallas.
 *   2. SE COMBINAN. Es lo que antes NO se podía: o elegías voz o elegías tema.
 *      Ahora «Miguel» + «Tengo miedo» + «corta» funcionan a la vez, que es
 *      justamente como busca alguien que sabe lo que necesita.
 *   3. CADA OPCIÓN LLEVA SU CUENTA, y las que no tienen nada no se ofrecen: no
 *      hay forma de llegar a «ninguna canalización».
 *   4. LO ELEGIDO SE VE Y SE QUITA DE UNO EN UNO, en fichas con su aspa, más un
 *      «limpiar» para dejarlo todo.
 *   5. EL NÚMERO DE RESULTADOS SIEMPRE VISIBLE, que es lo que dice si el filtro
 *      va bien o hay que aflojarlo.
 *
 * QUÉ SE ADAPTÓ A ESTE SITIO:
 *
 *   · Los dos filtros centrales NO son categorías: son las dos preguntas del
 *     lector —«hace un tiempo que…» y «me gustaría saber…»—. En un catálogo
 *     normal filtras por género; aquí filtras por lo que te pasa.
 *   · LAS ENSEÑANZAS NO ESTÁN, y es deliberado (Faina, 26-ago): «no son filtros,
 *     van en el plan 3».
 *   · La duración se ofrece en dos tramos y no en minutos exactos: quien filtra
 *     por tiempo quiere «algo corto», no «exactamente cuatro minutos».
 */

/* Para el buscador del desplegable: sin tildes ni mayusculas, que nadie escribe
   «Kjær» con su vocal exacta. */
const llano = (t) => String(t).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/* A partir de cuantas opciones el desplegable ofrece buscar. Con doce cabe
   mirarlas; con 248 —los canalizadores del corpus— recorrer la rueda es la
   parte mas incomoda del sitio. */
const BUSCAR_DESDE = 14;

const Desplegable = ({ nombre, opciones, valor, alElegir }) => {
  const { t } = useTranslation();
  /* Una opción con `clave` se muestra traducida (temas, duración); sin clave
     es un nombre propio (voz, canalizador) y se muestra tal cual. */
  const etiqueta = (o) => (o.clave ? t(o.clave) : o.texto);
  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const caja = useRef(null);
  const campo = useRef(null);

  /* Al abrir: la caja vacia y el cursor dentro, para poder escribir sin pulsar
     otra vez. */
  useEffect(() => {
    if (!abierto) { setFiltro(''); return; }
    if (campo.current) campo.current.focus();
  }, [abierto]);

  /* Se cierra al pulsar fuera y con Escape: lo que espera cualquiera. */
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [abierto]);

  const elegida = opciones.find((o) => o.id === valor);
  const vistas = filtro
    ? opciones.filter((o) => llano(etiqueta(o)).includes(llano(filtro)))
    : opciones;

  return (
    <Box className="fil-campo" ref={caja}>
      <button
        type="button"
        className={`fil-boton${elegida ? ' fil-boton-activo' : ''}`}
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
      >
        {elegida ? etiqueta(elegida) : nombre}
        <ChevronIcon className={`fil-chevron${abierto ? ' fil-chevron-abierto' : ''}`} />
      </button>

      {abierto && (
        <Box className="fil-menu" role="listbox">
          {/* 🔎 BUSCAR DENTRO DEL DESPLEGABLE (Faina, 1-sep). Solo aparece
              cuando hace falta: con pocas opciones estorba, con 248 es la
              diferencia entre encontrar a alguien y rendirse. */}
          {opciones.length >= BUSCAR_DESDE && (
            <input
              ref={campo}
              className="fil-buscar"
              type="text"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder={t('filtros.buscar_en', { n: opciones.length })}
              aria-label={t('filtros.buscar_x', { x: nombre.toLowerCase() })}
            />
          )}
          {elegida && (
            <button type="button" className="fil-opcion fil-opcion-todas"
              onClick={() => { alElegir(''); setAbierto(false); }}>
              {t('filtros.todas')}
            </button>
          )}
          {vistas.length === 0 && (
            <span className="fil-vacio">{t('filtros.nada_con', { x: filtro })}</span>
          )}
          {vistas.map((o) => (
            <button
              type="button"
              key={o.id}
              className={`fil-opcion${o.id === valor ? ' fil-opcion-elegida' : ''}`}
              onClick={() => { alElegir(o.id === valor ? '' : o.id); setAbierto(false); }}
              role="option"
              aria-selected={o.id === valor}
            >
              {etiqueta(o)}
              <span className="fil-opcion-n">{o.n}</span>
            </button>
          ))}
        </Box>
      )}
    </Box>
  );
};

/* Con el corpus del plan 4 el catalogo pasa de 102 a 5.880: el punto de los
   miles deja de ser un adorno. */
const miles = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const Filtros = ({ campos, activos, alElegir, alLimpiar, cuantas }) => {
  const { t } = useTranslation();
  const etiqueta = (o) => (o.clave ? t(o.clave) : o.texto);
  const puestos = campos
    .map((c) => ({ campo: c, opcion: c.opciones.find((o) => o.id === activos[c.clave]) }))
    .filter((x) => x.opcion);

  return (
    <Box className="fil">
      <Box className="fil-barra">
        {campos.map((c) => (
          <Desplegable
            key={c.clave}
            nombre={c.nombre}
            opciones={c.opciones}
            valor={activos[c.clave] || ''}
            alElegir={(v) => alElegir(c.clave, v)}
          />
        ))}

        {/* El número siempre a la vista: es lo que dice si el filtro va bien. */}
        <Typography className="fil-cuenta">
          {miles(cuantas)} {cuantas === 1 ? t('filtros.una') : t('filtros.varias')}
        </Typography>
      </Box>

      {puestos.length > 0 && (
        <Box className="fil-puestos">
          {puestos.map(({ campo, opcion }) => (
            <button
              type="button"
              key={campo.clave}
              className="fil-puesto"
              onClick={() => alElegir(campo.clave, '')}
              aria-label={t('filtros.quitar', { x: etiqueta(opcion) })}
            >
              {etiqueta(opcion)}<CloseIcon fontSize="inherit" />
            </button>
          ))}
          {puestos.length > 1 && (
            <button type="button" className="fil-limpiar" onClick={alLimpiar}>{t('filtros.limpiar')}</button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Filtros;
